import { Box, Typography, useTheme } from '@mui/material'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import { STEP_CHALLENGE_PROGRESS } from '../timing'
import { CountdownText } from './CountdownText'

interface StepChallengeProgressProps {
    progress: Array<number>
    currentPuzzle: number
    endTime: string
}

const getTileState = (
    index: number,
    progress: Array<number>,
    currentPuzzle: number,
): 'empty' | 'correct' | 'incorrect' | 'hint' | 'active' => {
    const puzzleStatus = progress[index]

    // Check progress status first
    if (puzzleStatus === 1) return 'correct'
    if (puzzleStatus === 0) return 'incorrect'
    if (puzzleStatus === 2) return 'hint'

    // Only show as active if not completed
    if (index === currentPuzzle - 1) return 'active'

    return 'empty'
}

const ProgressTile: React.FC<{
    state: 'empty' | 'correct' | 'incorrect' | 'hint' | 'active'
    number: number
    isActive: boolean
}> = ({ state, number, isActive }) => {
    const theme = useTheme()
    const isEmpty = state === 'empty' || state === 'active'
    const isFilled = state === 'correct' || state === 'incorrect' || state === 'hint'

    // Border colors
    const borderColor =
        state === 'incorrect'
            ? theme.palette.warning.light
            : state === 'correct'
              ? theme.palette.success.light
              : state === 'hint'
                ? theme.palette.orange.main
                : theme.palette.orange.main

    // Fill colors
    const fillColor =
        state === 'incorrect'
            ? theme.palette.warning.main
            : state === 'correct'
              ? theme.palette.success.main
              : state === 'hint'
                ? '#ffc000'
                : undefined

    return (
        <Box
            sx={{
                position: 'relative',
                width: '35px',
                height: { xs: '40px', md: '68px' },
                borderRadius: '6px',
            }}
        >
            {/* Main Frame */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: theme.palette.background.default,
                    border: `2px solid ${borderColor}`,
                    borderRadius: '6px',
                    zIndex: 1,
                }}
            />

            {/* Gradient Fill for completed states */}
            {isFilled && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '4px',
                        left: '4px',
                        right: '4px',
                        height: { xs: '32px', md: '60px' },
                        backgroundColor: fillColor,
                        borderRadius: '3px',
                        zIndex: 2,
                        transform: 'scale(0)',
                        opacity: 0,
                        animation: `fillAppear ${STEP_CHALLENGE_PROGRESS.fillAppearDuration}ms cubic-bezier(0.34, 1.56, 0.9, 1) forwards`,
                        animationDelay: `${isActive ? '250ms' : number * 150}ms`,
                        '@keyframes fillAppear': {
                            '0%': {
                                transform: 'scale(0)',
                                opacity: 0,
                                filter: 'brightness(1.5)',
                            },
                            '50%': {
                                transform: 'scale(1.1)',
                                filter: 'brightness(1.3)',
                            },
                            '100%': {
                                transform: 'scale(1)',
                                opacity: 1,
                                filter: 'brightness(1)',
                            },
                        },
                    }}
                >
                    {/* Inner gradient overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '3px',
                            left: '3px',
                            right: '3px',
                            height: { xs: '26px', md: '54px' },
                            borderRadius: '2px',
                            background:
                                state === 'correct'
                                    ? 'linear-gradient(180deg, #BFFF93 0%, #82C057 50%, #32693B 100%)'
                                    : state === 'incorrect'
                                      ? 'linear-gradient(180deg, #E87E7E 0%, #C05858 50%, #693232 100%)'
                                      : 'linear-gradient(180deg, #FBFE91 0%, #FFBF00 50%, #D77901 100%)',
                        }}
                    />
                </Box>
            )}

            {/* Glow Effect for active state */}
            {isActive && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: '-0.2px',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                >
                    {/* Multiple blur layers for golden glow */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: theme.palette.background.default,
                            border: `2px solid ${theme.palette.orange.main}`,
                            borderRadius: '6px',
                            filter: 'blur(7.5px)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: theme.palette.background.default,
                            border: `2px solid ${theme.palette.orange.main}`,
                            borderRadius: '6px',
                            filter: 'blur(6px)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: theme.palette.background.default,
                            border: `2px solid ${theme.palette.orange.main}`,
                            borderRadius: '6px',
                            filter: 'blur(4px)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: theme.palette.background.default,
                            border: `2px solid ${theme.palette.orange.main}`,
                            borderRadius: '6px',
                            filter: 'blur(2px)',
                        }}
                    />
                </Box>
            )}

            {/* Number Display for empty states only */}
            {isEmpty && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 3,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Space Grotesk',
                            fontWeight: 700,
                            fontSize: { xs: '16px', md: '20px' },
                            color: isActive ? theme.palette.orange.main : `rgba(255, 197, 140, 0.25)`,
                            lineHeight: 'normal',
                            textAlign: 'center',
                            textTransform: 'uppercase',
                        }}
                    >
                        {number}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

export const StepChallengeProgress: React.FC<StepChallengeProgressProps> = ({
    progress: _progress,
    currentPuzzle: _currentPuzzle,
    endTime,
}) => {
    const theme = useTheme()
    const getPuzzle = useStoreActions((actions) => actions.puzzleQuestView.getPuzzle)

    // Read directly from store
    const stepProgress = useStoreState((state) => state.puzzleQuestView.stepProgress)
    const currentPuzzle = useStoreState((state) => state.puzzleQuestView.currentPuzzleNumber)
    const puzzleState = useStoreState((state) => state.puzzleQuestView.puzzleState)
    const progress = stepProgress.progress

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, md: 1 }, width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    textAlign: 'center',
                    width: '100%',
                    lineHeight: 0,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        color: theme.palette.secondary.main,
                        textTransform: 'uppercase',
                        lineHeight: 'normal',
                        width: '100%',
                    }}
                >
                    DAILY CHALLENGE
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: { xs: '40px', md: '68px' },
                    width: '100%',
                }}
            >
                {Array.from({ length: 10 }, (_, i) => {
                    const tileState = getTileState(i, progress, currentPuzzle)
                    const isActive = i === currentPuzzle - 1
                    return <ProgressTile key={i} state={tileState} number={i + 1} isActive={isActive} />
                })}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    textAlign: 'center',
                    width: '100%',
                    lineHeight: 0,
                    mb: { xs: -0.5, md: 0 },
                }}
            >
                <CountdownText
                    endTime={endTime}
                    callback={getPuzzle}
                    prefix="Time Remaining: "
                    variant="body1"
                    sx={{
                        fontSize: { xs: '18px', md: '20px' },
                        color: theme.palette.orange.main,
                        textTransform: 'capitalize',
                        lineHeight: 'normal',
                        width: '100%',
                    }}
                />
            </Box>
        </Box>
    )
}
